import {
  ArrowLeftRight,
  BarChart3,
  Bell,
  FileCheck,
  FileUp,
  ListFilter,
  type LucideIcon,
  Percent,
  ReceiptEuro,
  RotateCcw,
  Wallet,
} from 'lucide-react';
import { defaultBook, type FinanceBook, type FinanceSession, financeHref } from './book';
import { CLIENT_DASHBOARD_PATH, FINANCE_CANONICAL_ROOT, splitHref, withoutTrailingSlash } from './paths';

export type FinanceGrant = 'wildcard' | 'client';

export type FinanceBookRule = 'operator' | 'client' | 'both';

export type FinanceDenial = 'open-operator-book' | 'back-to-finance';

export const FINANCE_NAV_GROUP_ORDER = ['top', 'Operations', 'Review and Reporting'] as const;

export type FinanceNavGroupId = (typeof FINANCE_NAV_GROUP_ORDER)[number];

export type FinanceSurfaceId =
  | 'operator-dashboard'
  | 'client-dashboard'
  | 'bank-statements'
  | 'bank-rules'
  | 'psp-matching'
  | 'payouts'
  | 'transactions'
  | 'alerts'
  | 'refunds'
  | 'invoices'
  | 'pnl';

export interface FinanceSurface {
  id: FinanceSurfaceId;
  path: string;
  match: 'exact' | 'prefix';
  grant: FinanceGrant;
  book: FinanceBookRule;
  nav?: {
    name: string;
    group: FinanceNavGroupId;
    icon: LucideIcon;
    /** RBAC. `operator` is the operator-role allowlist. It is not the grant and is not the book. */
    visibility: 'any' | 'operator';
  };
}

/**
 * One row per finance surface. Nav, the route gate, the header and the tour read this.
 * Paths are canonical and matched after the cell prefix is stripped.
 */
export const FINANCE_SURFACES: readonly FinanceSurface[] = [
  {
    id: 'operator-dashboard',
    path: FINANCE_CANONICAL_ROOT,
    match: 'exact',
    grant: 'wildcard',
    book: 'operator',
    nav: { name: 'Dashboard', group: 'top', icon: BarChart3, visibility: 'any' },
  },
  {
    id: 'client-dashboard',
    path: CLIENT_DASHBOARD_PATH,
    match: 'exact',
    grant: 'client',
    book: 'client',
    nav: { name: 'Dashboard', group: 'top', icon: BarChart3, visibility: 'any' },
  },
  {
    id: 'bank-statements',
    path: `${FINANCE_CANONICAL_ROOT}/bank-statements`,
    match: 'prefix',
    grant: 'wildcard',
    book: 'operator',
    nav: { name: 'Bank Statements', group: 'Operations', icon: FileUp, visibility: 'operator' },
  },
  {
    id: 'bank-rules',
    path: `${FINANCE_CANONICAL_ROOT}/bank-rules`,
    match: 'prefix',
    grant: 'wildcard',
    book: 'operator',
    nav: { name: 'Bank Rules', group: 'Operations', icon: ListFilter, visibility: 'operator' },
  },
  {
    id: 'psp-matching',
    path: `${FINANCE_CANONICAL_ROOT}/psp-matching`,
    match: 'prefix',
    grant: 'wildcard',
    book: 'operator',
    nav: { name: 'PSP Matching', group: 'Operations', icon: ReceiptEuro, visibility: 'operator' },
  },
  {
    id: 'payouts',
    path: `${FINANCE_CANONICAL_ROOT}/payouts`,
    match: 'prefix',
    grant: 'wildcard',
    book: 'both',
    nav: { name: 'Client Payouts', group: 'Operations', icon: Wallet, visibility: 'any' },
  },
  {
    id: 'transactions',
    path: `${FINANCE_CANONICAL_ROOT}/transactions`,
    match: 'prefix',
    grant: 'client',
    book: 'both',
    nav: { name: 'Transactions', group: 'Operations', icon: ArrowLeftRight, visibility: 'any' },
  },
  {
    id: 'alerts',
    path: `${FINANCE_CANONICAL_ROOT}/alerts`,
    match: 'prefix',
    grant: 'client',
    book: 'both',
    nav: { name: 'Alerts', group: 'Review and Reporting', icon: Bell, visibility: 'operator' },
  },
  {
    id: 'refunds',
    path: `${FINANCE_CANONICAL_ROOT}/refunds`,
    match: 'prefix',
    grant: 'client',
    book: 'both',
    nav: { name: 'Refunds', group: 'Review and Reporting', icon: RotateCcw, visibility: 'any' },
  },
  {
    id: 'invoices',
    path: `${FINANCE_CANONICAL_ROOT}/invoices`,
    match: 'prefix',
    grant: 'client',
    book: 'both',
    nav: { name: 'Invoices', group: 'Review and Reporting', icon: FileCheck, visibility: 'any' },
  },
  {
    id: 'pnl',
    path: `${FINANCE_CANONICAL_ROOT}/pnl`,
    match: 'prefix',
    grant: 'client',
    book: 'both',
    nav: { name: 'P&L', group: 'Review and Reporting', icon: Percent, visibility: 'operator' },
  },
];

export interface FinanceAccess {
  book: FinanceBook;
  wildcard: boolean;
  /** False while the clients query is loading, failed, or has never settled. */
  ready: boolean;
  /** A clients fetch has completed at least once; lets a refetch fail closed instead of flashing open. */
  resolved?: boolean;
  clientIds: readonly string[];
}

/** The FinanceAccess of a live session. `resolved` says whether the grant has data to judge a surface by. */
export function financeAccessOf(session: FinanceSession): FinanceAccess {
  return {
    book: session.book,
    wildcard: session.wildcard,
    ready: session.ready,
    resolved: session.wildcard || session.clientIds.length > 0,
    clientIds: session.clientIds,
  };
}

function surfaceMatches(surface: FinanceSurface, canonicalPath: string): boolean {
  if (surface.match === 'exact') return canonicalPath === surface.path;
  return canonicalPath === surface.path || canonicalPath.startsWith(`${surface.path}/`);
}

export function matchFinanceSurface(logicalPath: string): FinanceSurface | null {
  const canonical = withoutTrailingSlash(splitHref(logicalPath).path);
  const matches = FINANCE_SURFACES.filter((surface) => surfaceMatches(surface, canonical));
  if (matches.length === 0) return null;
  return matches.sort((left, right) => {
    const byLength = right.path.length - left.path.length;
    if (byLength !== 0) return byLength;
    if (left.match === right.match) return 0;
    return left.match === 'exact' ? -1 : 1;
  })[0];
}

export function surfaceOpen(surface: FinanceSurface, book: FinanceBook, wildcard: boolean): boolean {
  if (surface.grant === 'wildcard' && !wildcard) return false;
  switch (surface.book) {
    case 'operator':
      return book.kind === 'operator';
    case 'client':
      return book.kind === 'client' || book.kind === 'mine';
    case 'both':
      return true;
    default: {
      const unreachable: never = surface.book;
      return unreachable;
    }
  }
}

/**
 * True when this session can open the route. An unresolved session fails closed.
 *
 * Grant membership of a specific client id is *not* checked here: `/users/me/clients`
 * is paginated and can be truncated, so a client-side check could deny a book the
 * server would allow. FGA intersects the grant on the API instead and returns 403.
 */
export function routeOpen(logicalPath: string, access: FinanceAccess): boolean {
  const surface = matchFinanceSurface(logicalPath);
  if (!surface) return true;
  return access.ready && surfaceOpen(surface, access.book, access.wildcard);
}

export function denialFor(surface: FinanceSurface, access: FinanceAccess): FinanceDenial | null {
  // Fail closed during a refetch when the last-known grant can judge the surface;
  // a never-resolved session stays open so a cold load never flashes a denial.
  if (!access.ready && !access.resolved) return null;
  if (surfaceOpen(surface, access.book, access.wildcard)) return null;
  if (access.wildcard && access.book.kind !== 'operator' && surface.book === 'operator') return 'open-operator-book';
  return 'back-to-finance';
}

export function denialHref(recovery: FinanceDenial, logicalPath: string, access: FinanceAccess): string {
  switch (recovery) {
    case 'open-operator-book':
      return financeHref(logicalPath, { kind: 'operator' });
    case 'back-to-finance': {
      const home = access.wildcard ? FINANCE_CANONICAL_ROOT : CLIENT_DASHBOARD_PATH;
      const book = access.wildcard ? ({ kind: 'operator' } as const) : defaultBook(false, access.clientIds);
      return financeHref(home, book);
    }
    default: {
      const unreachable: never = recovery;
      return unreachable;
    }
  }
}

/** Nav rows the session can open. Hidden, not disabled. Wildcard-only rows wait until the clients query settles. */
export function visibleFinanceSurfaces(access: FinanceAccess): FinanceSurface[] {
  const wildcard = access.ready ? access.wildcard : false;
  return FINANCE_SURFACES.filter((surface) => surface.nav && surfaceOpen(surface, access.book, wildcard));
}
